import { t } from "i18next";
import { CloudOff, CloudUpload } from "lucide-react";
import type { JSX } from "react";
import { AppConfig } from "@/common/appConfig";
import { useVisitScope } from "@/lib/visit-queries";
import {
  isBackgroundUploadVisible,
  useBackgroundVisitUploadStore,
} from "@/store/backgroundVisitUpload.store";
import { BackgroundVisitUploadItem } from "./BackgroundVisitUploadItem";

export const BackgroundVisitUploadPanel = (): JSX.Element => {
  const {
    uploads: allUploads,
    cancelVisitToUpload,
    startVisitToUpload,
    restartRejectedAudioAttachment,
  } = useBackgroundVisitUploadStore();
  const scope = useVisitScope();
  const uploads = allUploads.filter((upload) =>
    isBackgroundUploadVisible(upload, scope),
  );

  const v3 = AppConfig.enableAppRedesign === true;
  return (
    <div
      className={
        v3
          ? "max-h-[min(60dvh,420px)] divide-y divide-v3-border overflow-y-auto font-v3"
          : "divide-y"
      }
    >
      {uploads.length === 0 && v3 ? (
        <div
          className="flex flex-col items-center px-6 pb-6 pt-4 text-center"
          data-testid="uploads-empty"
        >
          <span
            aria-hidden
            className="flex size-10 items-center justify-center rounded-full bg-v3-surface-2 text-v3-text-2"
          >
            <CloudUpload className="size-5" />
          </span>
          <p className="mt-3 text-[15px] font-semibold text-v3-text">
            {t("backgroundVisitUpload.emptyTitle")}
          </p>
          <p className="mt-1 max-w-[260px] text-[13.5px] leading-5 text-v3-text-3">
            {t("backgroundVisitUpload.emptyHint")}
          </p>
        </div>
      ) : uploads.length === 0 ? (
        <div className="flex items-center justify-center gap-2">
          <CloudOff className="text-primary" />
          <span className="text-sm">
            {t("backgroundVisitUpload.emptyFiles")}
          </span>
        </div>
      ) : (
        uploads.map((backgroundVisit) => (
          <BackgroundVisitUploadItem
            key={backgroundVisit.id}
            backgroundVisitUpload={backgroundVisit}
            onCancel={() => {
              cancelVisitToUpload(backgroundVisit.id);
            }}
            onRestart={() => {
              void restartRejectedAudioAttachment(backgroundVisit.id).catch(
                () => undefined,
              );
            }}
            onStart={() => {
              void startVisitToUpload(backgroundVisit.id);
            }}
          />
        ))
      )}
    </div>
  );
};
